import { useTranslation } from 'react-i18next';

import {
	Button,
	CheveronSmallLeftOutline,
	CogOutline,
	Flex,
	HStack,
	IconButton,
	LeftDrawerCollapseOutline,
	MaximizeOutline,
	PencilAltOutline,
	Text,
	Tooltip,
	ViewGridAddOutline,
	VStack,
	XOutline,
} from '@bonusly/design-system';

import { BizyConversationHistoryList } from './components/bizy-conversation-history-list';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';

interface BizySidePanelHistoryDrawerProps {
	activeConversationId?: string;
	isMobileMode?: boolean;
	onNewConversation: () => void;
	onResetChat: () => void;
	showFullscreenToggle?: boolean;
}

export function BizySidePanelHistoryDrawer({
	activeConversationId,
	isMobileMode = false,
	onNewConversation,
	onResetChat,
	showFullscreenToggle = true,
}: BizySidePanelHistoryDrawerProps) {
	const { t } = useTranslation('admin_bizy');
	const { t: tSettings } = useTranslation('bizy_settings');
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const closeHistoryDrawer = useBizySidePanelStore((state) => state.closeHistoryDrawer);
	const showSettings = useBizySidePanelStore((state) => state.showSettings);
	const toggleFullscreen = useBizySidePanelStore((state) => state.toggleFullscreen);
	const isFullscreenLayout = isFullscreen && !isMobileMode;
	const drawerFontSize = isFullscreenLayout || isMobileMode ? 'md' : 'sm';

	const handleNewConversation = () => {
		onNewConversation();
		if (!isFullscreenLayout) closeHistoryDrawer();
	};

	const handleClosePanel = () => {
		closePanel();
	};

	const handleConversationLoaded = () => {
		if (!isFullscreenLayout) closeHistoryDrawer();
	};

	const handleOpenConnectors = () => {
		showSettings('connections');
	};

	const actionButtonProps = {
		justifyContent: 'flex-start',
		variant: 'ghost' as const,
		colorScheme: 'secondary' as const,
		size: 'sm' as const,
		gap: 2,
		px: 2.5,
		py: 2,
		h: 'auto',
		borderRadius: 'md',
		sx: {
			'.chakra-button__icon': {
				h: '20px',
				m: 0,
				w: '20px',
				'& svg': {
					h: '20px',
					w: '20px',
				},
			},
		},
	};

	return (
		<Flex
			w="100%"
			h="100%"
			minH={0}
			flexShrink={0}
			direction="column"
			bg="bg.surface"
			borderRight={isFullscreenLayout ? '1px solid' : 'none'}
			borderColor="border.soft"
			data-menu-size={drawerFontSize}
		>
			<Flex align="center" justify="space-between" px={isFullscreenLayout ? 5 : 3} py={3} minH="68px" flexShrink={0}>
				<HStack spacing={1} minW={0}>
					{!isFullscreenLayout && (
						<Tooltip label={t('sidePanel.backToChat')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.backToChat')}
								icon={<CheveronSmallLeftOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={closeHistoryDrawer}
							/>
						</Tooltip>
					)}
					<Text size="md" fontWeight="semibold" color="text.default" noOfLines={1}>
						{t('sidePanel.askBizy')}
					</Text>
				</HStack>
				{!isFullscreenLayout && showFullscreenToggle && (
					<HStack spacing={0}>
						<Tooltip label={t('sidePanel.fullscreen')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.fullscreen')}
								icon={<MaximizeOutline boxSize={4} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={toggleFullscreen}
							/>
						</Tooltip>
						<Tooltip label={t('sidePanel.close')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.close')}
								icon={<XOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={handleClosePanel}
							/>
						</Tooltip>
					</HStack>
				)}
				{isFullscreenLayout && (
					<Tooltip label={t('sidePanel.closeBizyMenu')} hasArrow={false}>
						<IconButton
							aria-label={t('sidePanel.closeBizyMenu')}
							icon={<LeftDrawerCollapseOutline boxSize={5} />}
							variant="ghost"
							size="sm"
							color="secondary.fg"
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={closeHistoryDrawer}
						/>
					</Tooltip>
				)}
			</Flex>

			<BizyConversationHistoryList
				activeConversationId={activeConversationId}
				fontSize={drawerFontSize}
				searchHeight={10}
				searchPb={4}
				searchPt={0}
				searchPx={5}
				onConversationLoaded={handleConversationLoaded}
				onResetChat={onResetChat}
			>
				<VStack align="stretch" spacing={0} px={3} pb={4} flexShrink={0}>
					<Button leftIcon={<PencilAltOutline boxSize="20px" />} onClick={handleNewConversation} {...actionButtonProps}>
						<Text as="span" size={drawerFontSize} variant="medium">
							{t('sidePanel.newChat')}
						</Text>
					</Button>
					<Button leftIcon={<ViewGridAddOutline boxSize="20px" />} onClick={handleOpenConnectors} {...actionButtonProps}>
						<Text as="span" size={drawerFontSize} variant="medium">
							{tSettings('connectors')}
						</Text>
					</Button>
					<Button leftIcon={<CogOutline boxSize="20px" />} onClick={() => showSettings()} {...actionButtonProps}>
						<Text as="span" size={drawerFontSize} variant="medium">
							{tSettings('title')}
						</Text>
					</Button>
				</VStack>

				<Text px={5} pb={1} size={drawerFontSize} fontWeight="medium" color="text.softest">
					{t('sidePanel.recentConversations')}
				</Text>
			</BizyConversationHistoryList>
		</Flex>
	);
}
